import { queryOne, queryAll } from '../../core/dom.js';
import {
  normalizeTycoonAvatarId,
  tycoonAvatarLabel
} from '../../../shared/tycoon-avatars.js';
import { tycoonAvatarSvg, tycoonAvatarOptionsMarkup } from './avatar-art.js';

export function renderTycoonLobby({
  state,
  roomCode,
  userSeat,
  pendingSeatClaim,
  pendingAvatarId,
  startPending
}) {
  if (!state || state.gameType !== 'tycoon') return;

  const seats = Array.isArray(state.seats) ? state.seats : [];
  const isWaiting = state.status === 'waiting';
  const occupiedCount = seats.filter(seat => seat.occupied).length;

  queryOne('#tycoonActiveRoomCode').textContent = state.room?.code || roomCode;
  queryOne('#tycoonOnlineRoomCode').textContent = state.room?.code || roomCode;
  queryOne('#tycoonHostSpectatorControl').classList.toggle('hidden', !state.room?.isHost);
  queryOne('#tycoonHostChangeGameBtn').classList.toggle('hidden', !(state.room?.isHost && isWaiting));
  queryOne('#tycoonAllowSpectatorsToggle').checked = Boolean(state.room?.allowSpectators);
  queryOne('#tycoonSpectatorBtn').disabled = !state.room?.allowSpectators;
  queryOne('#tycoonSpectatorStatus').textContent = state.room?.allowSpectators
    ? `${state.spectatorsCount || 0} khán giả đang xem`
    : 'Chủ phòng đang tắt quyền khán giả';

  queryAll('[data-tycoon-seat]').forEach(button => {
    const seatIndex = Number(button.dataset.tycoonSeat);
    const seat = seats[seatIndex] || { occupied: false, connected: false };
    const mine = userSeat === seatIndex;
    const pending = pendingSeatClaim === seatIndex;
    const lobbyBusy = Number.isInteger(pendingSeatClaim) || Boolean(pendingAvatarId) || startPending;
    const status = button.querySelector(`[data-seat-status="${seatIndex}"]`);
    const avatarId = normalizeTycoonAvatarId(seat.avatarId, seatIndex);
    const avatar = button.querySelector(`[data-seat-avatar="${seatIndex}"]`) || button.querySelector('.side-symbol');
    const name = button.querySelector(`[data-seat-name="${seatIndex}"]`) || button.querySelector('.side-copy strong');

    if (avatar) {
      avatar.innerHTML = tycoonAvatarSvg(avatarId, { fallbackIndex: seatIndex });
      avatar.setAttribute('aria-label', tycoonAvatarLabel(avatarId, seatIndex));
    }
    if (name) {
      name.textContent = seat.occupied && seat.displayName
        ? `${seat.displayName}${mine ? ' · Bạn' : ''}`
        : `P${seatIndex + 1} · ${tycoonAvatarLabel(avatarId, seatIndex)}`;
    }

    button.classList.toggle('is-pending', pending);
    button.setAttribute('aria-busy', pending ? 'true' : 'false');

    if (pending) {
      status.textContent = 'Đang chọn ghế…';
      button.disabled = true;
      button.style.opacity = '1';
    } else if (seat.occupied) {
      status.textContent = mine
        ? (seat.connected ? '● Ghế của bạn · Đã kết nối' : '● Ghế của bạn')
        : (seat.connected ? '● Đã có người chơi' : '○ Đang chờ kết nối lại');
      button.disabled = lobbyBusy || !mine || !isWaiting;
      button.style.opacity = mine ? '1' : '0.62';
    } else {
      status.textContent = isWaiting ? 'Sẵn sàng chọn' : 'Không thể vào giữa ván';
      button.disabled = lobbyBusy || !isWaiting;
      button.style.opacity = isWaiting ? '1' : '0.55';
    }

    button.setAttribute('aria-pressed', mine ? 'true' : 'false');
  });

  const avatarPicker = queryOne('#tycoonLobbyAvatarPicker');
  const avatarOptions = queryOne('#tycoonLobbyAvatarOptions');
  const avatarNote = queryOne('#tycoonLobbyAvatarNote');
  const canChooseAvatar = Boolean(isWaiting && Number.isInteger(userSeat) && seats[userSeat]?.occupied);
  if (avatarPicker) avatarPicker.classList.toggle('hidden', !canChooseAvatar);

  if (canChooseAvatar && avatarOptions) {
    const mine = seats[userSeat];
    const selectedId = normalizeTycoonAvatarId(mine.avatarId, userSeat);
    const usedIds = seats
      .filter((seat, index) => index !== userSeat && seat.occupied)
      .map((seat, index) => normalizeTycoonAvatarId(seat.avatarId, seat.index ?? index));

    avatarOptions.innerHTML = tycoonAvatarOptionsMarkup({
      selectedId,
      usedIds,
      disabled: Boolean(pendingAvatarId) || startPending,
      dataAttribute: 'data-tycoon-online-avatar'
    });

    if (avatarNote) {
      avatarNote.textContent = pendingAvatarId
        ? `Đang đổi sang ${tycoonAvatarLabel(pendingAvatarId, userSeat)}…`
        : `Nhân vật của bạn: ${tycoonAvatarLabel(selectedId, userSeat)} · Mỗi người dùng một con vật riêng.`;
    }
  } else if (avatarOptions) {
    avatarOptions.innerHTML = '';
  }

  const startBtn = queryOne('#tycoonStartOnlineBtn');
  const isHost = Boolean(state.room?.isHost);
  startBtn.classList.toggle('hidden', !isHost);
  startBtn.setAttribute('aria-busy', startPending ? 'true' : 'false');
  startBtn.disabled =
    !isHost ||
    !isWaiting ||
    occupiedCount < 2 ||
    Number.isInteger(pendingSeatClaim) ||
    Boolean(pendingAvatarId) ||
    startPending;

  startBtn.textContent = startPending
    ? 'Đang bắt đầu…'
    : occupiedCount < 2
      ? `Cần thêm người chơi (${occupiedCount}/2)`
      : `Bắt đầu ván · ${occupiedCount} người`;

  if (isWaiting) {
    queryOne('#tycoonSeatPickerDesc').textContent = Number.isInteger(userSeat)
      ? `Phòng ${roomCode} · Bạn đang giữ ghế ${userSeat + 1}. ${isHost ? 'Khi đủ từ 2 người, bấm Bắt đầu.' : 'Đang chờ chủ phòng bắt đầu.'}`
      : `Phòng ${roomCode} · Chọn một trong 4 ghế để tham gia.`;
  }
}
